import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, type Locale } from '@barev/i18n';
import { navigate, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, Skeleton } from '../components/kit.tsx';
import { CopyLink } from './Agencies.tsx';

type Deal = 'PROFIT_SHARE' | 'COMMISSION';
type Terms = { deal: Deal; commissionPct: number; profitSharePct: number; fotPct: number; taxPct: number };

function useTermsText() {
  const { t } = useT();
  return (x: Terms) => (x.deal === 'COMMISSION' ? t('sp.terms.COMMISSION', { pct: x.commissionPct }) : t('sp.terms.PROFIT_SHARE', { pct: x.profitSharePct, fot: x.fotPct, tax: x.taxPct }));
}

/** Партнёры услуг: клининговые компании со своими сотрудниками и менеджером */
export function ServicePartners() {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const terms = useTermsText();
  const list = useQuery(trpc.bo.partners.servicePartners.queryOptions());
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('sp.title')}</h1>
        <button type="button" className="bo-btn bo-btn--accent" onClick={() => setCreating(true)}>
          <I name="plus" size={16} />
          {t('sp.add')}
        </button>
      </div>
      <p className="bo-hint" style={{ marginTop: -6, marginBottom: 14 }}>
        {t('sp.lead')}
      </p>
      {!list.data ? (
        <Skeleton h={240} />
      ) : list.data.length === 0 ? (
        <div className="bo-panel bo-empty">{t('sp.empty')}</div>
      ) : (
        <div className="bo-panel" style={{ padding: 8 }}>
          <table className="bo-table bo-ptable">
            <thead>
              <tr>
                <th>{t('sp.col.name')}</th>
                <th className="bo-r">{t('sp.col.staff')}</th>
                <th className="bo-r">{t('sp.col.active')}</th>
                <th className="bo-r">{t('sp.col.month')}</th>
                <th className="bo-r">{t('sp.col.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/service-partners/${c.id}`)} data-off={!c.active || undefined}>
                  <td>
                    <a href={`#/service-partners/${c.id}`} className="bo-who" onClick={(e) => e.stopPropagation()}>
                      <Avatar name={c.name} id={c.id} size={38} />
                      <span style={{ minWidth: 0 }}>
                        <span className="bo-who__name">{c.name}</span>
                        <span className="bo-who__meta">{terms(c.terms)}</span>
                        <span className="bo-tags" style={{ marginTop: 4 }}>
                          {!c.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
                          {c.managers === 0 && <span className="bo-chip bo-chip--warn">{t('sp.noManager')}</span>}
                          {c.autoAssign && <span className="bo-chip">{t('sp.auto')}</span>}
                        </span>
                      </span>
                    </a>
                  </td>
                  <td className="bo-r bo-num">{c.staff}</td>
                  <td className="bo-r bo-num">{c.activeOrders}</td>
                  <td className="bo-r bo-num">{amd(c.thisMonth)}</td>
                  <td className="bo-r">
                    <b className="bo-num">{amd(c.balance)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <PartnerDialog onClose={() => setCreating(false)} />}
    </>
  );
}

export function ServicePartnerCard({ id }: { id: string }) {
  const { t, amd, date, dateTime, locale, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const terms = useTermsText();
  const c = useQuery(trpc.bo.partners.servicePartner.queryOptions({ id }));
  const [dlg, setDlg] = useState<'edit' | 'payout' | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: trpc.bo.partners.servicePartner.queryKey({ id }) });
    void qc.invalidateQueries({ queryKey: trpc.bo.partners.servicePartners.queryKey() });
  };
  const reset = useMutation(trpc.bo.partners.resetManagerLink.mutationOptions({ onSuccess: () => (refresh(), toast(t('sp.linkReset'))), onError: (e) => toast(errText(t, e), 'error') }));
  const removeManager = useMutation(trpc.bo.partners.removeManager.mutationOptions({ onSuccess: refresh, onError: (e) => toast(errText(t, e), 'error') }));
  const back = (
    <a className="bo-back" href="#/service-partners">
      <I name="back" size={16} />
      {t('sp.back')}
    </a>
  );
  if (!c.data)
    return (
      <>
        {back}
        {c.isError ? <div className="bo-panel bo-empty">{errText(t, c.error)}</div> : <Skeleton h={300} />}
      </>
    );
  const d = c.data;
  return (
    <>
      {back}
      <header className="bo-profile">
        <Avatar name={d.name} id={d.id} size={64} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 className="bo-title" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
            {d.name}
          </h1>
          <div className="bo-muted" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span>{terms(d.terms)}</span>
            {d.contactPhone && <span>{d.contactPhone}</span>}
            <span>{t('prv.since', { date: date(d.createdAt) })}</span>
            {d.autoAssign && <span className="bo-chip">{t('sp.auto')}</span>}
            {!d.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
          </div>
        </div>
        <button type="button" className="bo-btn" onClick={() => setDlg('edit')}>
          {t('prv.edit')}
        </button>
      </header>

      <div className="bo-client-grid">
        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('sp.managerLink')}</h2>
              <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" disabled={reset.isPending} onClick={() => confirm(t('sp.resetConfirm')) && reset.mutate({ id })}>
                {t('sp.resetLink')}
              </button>
            </div>
            <p className="bo-hint" style={{ marginBottom: 10 }}>
              {t('sp.managerLinkHint')}
            </p>
            <CopyLink url={d.managerLink} />
            <h3 className="bo-label" style={{ marginTop: 16 }}>
              {t('sp.managers')}
            </h3>
            {d.managers.length === 0 ? (
              <p className="bo-hint">{t('sp.noManagers')}</p>
            ) : (
              <ul className="bo-list">
                {d.managers.map((m) => (
                  <li key={m.id}>
                    <span className="bo-grow">
                      <b>{m.name}</b>
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        {t('sp.managerSince', { date: date(m.since) })}
                      </span>
                    </span>
                    <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => confirm(t('sp.removeManagerConfirm', { name: m.name })) && removeManager.mutate({ id: m.id })}>
                      {t('sp.remove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('sp.staff')}</h2>
              <span className="bo-faint bo-small">{t('sp.staffHint')}</span>
            </div>
            {d.staff.length === 0 ? (
              <p className="bo-hint">{t('sp.noStaff')}</p>
            ) : (
              <ul className="bo-list">
                {d.staff.map((s) => (
                  <li key={s.id} style={{ opacity: s.active ? 1 : 0.5 }}>
                    <a href={`#/providers/${s.id}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <b>{s.name}</b>
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        {[s.phone, s.maxOrdersPerDay ? t('sp.limitOrders', { n: s.maxOrdersPerDay }) : null, s.maxHoursPerDay ? t('sp.limitHours', { n: s.maxHoursPerDay }) : null].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </a>
                    {!s.linked && <span className="bo-chip bo-chip--warn">{t('sp.notLinked')}</span>}
                    {!s.active && <span className="bo-chip">{t('sp.off')}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('sp.orders')}</h2>
            </div>
            {d.orders.length === 0 ? (
              <p className="bo-hint">{t('sp.noOrders')}</p>
            ) : (
              <ul className="bo-list">
                {d.orders.map((o) => (
                  <li key={o.id}>
                    <a href={`#/orders/${o.id}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <b>
                        #{o.number} · {shared(`services.${o.service}.name`)}
                      </b>
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        {dateTime(o.scheduledAt)} · {o.staff ?? t('sp.waitingStaff')}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="bo-col">
          <section className="bo-panel bo-money-card">
            <span className="bo-label" style={{ color: 'inherit', opacity: 0.75 }}>
              {t('sp.toPay')}
            </span>
            <div className="bo-bignum" style={{ margin: '6px 0 8px' }}>
              {amd(d.money.balance)}
            </div>
            <div className="bo-small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <span>
                {t('ag.accrued')} <b className="bo-num">{amd(d.money.accrued)}</b>
              </span>
              <span>
                {t('prv.paidTotal')} <b className="bo-num">{amd(d.money.paid)}</b>
              </span>
            </div>
            <button type="button" className="bo-btn bo-btn--sm" disabled={d.money.balance <= 0} onClick={() => setDlg('payout')}>
              {t('prv.payout')}
            </button>
            {d.money.months.length > 0 && (
              <ul className="bo-list" style={{ marginTop: 12 }}>
                {d.money.months.map((m) => (
                  <li key={m.period}>
                    <span className="bo-grow" style={{ textTransform: 'capitalize' }}>
                      {formatDateTime(locale as Locale, new Date(`${m.period}-15T12:00:00+04:00`), { month: 'long', year: 'numeric' })}
                    </span>
                    <span className="bo-small" style={{ opacity: 0.75 }}>
                      {t('ag.orders', { n: m.orders })} · {amd(m.revenue)}
                    </span>
                    <b className="bo-num">{amd(m.amount)}</b>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('sp.byOrder')}</h2>
            </div>
            {d.money.recent.length === 0 ? (
              <p className="bo-hint">{t('sp.noIncome')}</p>
            ) : (
              <ul className="bo-list">
                {d.money.recent.slice(0, 20).map((e) => (
                  <li key={e.orderId}>
                    <a href={`#/orders/${e.orderId}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <b>#{e.number}</b> <span className="bo-small bo-faint">{date(e.at)}</span>
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        {t('sp.paid', { sum: amd(e.paid) })}
                        {e.deal === 'PROFIT_SHARE' ? ` · ${t('sp.fotTax', { fot: amd(e.fot), tax: amd(e.tax) })}` : ` · −${e.pct}%`}
                      </span>
                    </a>
                    <b className="bo-num">{amd(e.amount)}</b>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {d.note && (
            <section className="bo-panel">
              <p className="bo-side-panel__note" style={{ margin: 0 }}>
                {d.note}
              </p>
            </section>
          )}
        </div>
      </div>
      {dlg === 'edit' && <PartnerDialog partner={d} onClose={() => setDlg(null)} onDone={refresh} />}
      {dlg === 'payout' && <PartnerPayout id={d.id} balance={d.money.balance} onClose={() => setDlg(null)} onDone={refresh} />}
    </>
  );
}

type PartnerRow = {
  id: string;
  name: string;
  contactPhone: string | null;
  note: string | null;
  active: boolean;
  deal: Deal;
  commissionPct: number;
  profitSharePct: number;
  fotPct: number | null;
  taxPct: number | null;
  defaults: { fotPct: number; taxPct: number };
};

function PartnerDialog({ partner, onClose, onDone }: { partner?: PartnerRow; onClose: () => void; onDone?: () => void }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ ...trpc.bo.settings.get.queryOptions(), enabled: !partner });
  const defaults = partner?.defaults ?? { fotPct: settings.data?.['partner.fotPct'] ?? 50, taxPct: settings.data?.['partner.taxPct'] ?? 10 };
  const [f, setF] = useState({
    name: partner?.name ?? '',
    contactPhone: partner?.contactPhone ?? '',
    note: partner?.note ?? '',
    active: partner?.active ?? true,
    deal: partner?.deal ?? ('COMMISSION' as Deal),
    commissionPct: String(partner?.commissionPct ?? 30),
    profitSharePct: String(partner?.profitSharePct ?? 50),
    fotPct: partner?.fotPct == null ? '' : String(partner.fotPct),
    taxPct: partner?.taxPct == null ? '' : String(partner.taxPct),
  });
  const num = (s: string) => Math.min(100, Number(s) || 0);
  const opt = (s: string) => (s === '' ? null : num(s));
  // пример на 20 000 ֏, чтобы было видно, как считается
  const ex = 20_000;
  const fot = opt(f.fotPct) ?? defaults.fotPct;
  const tax = opt(f.taxPct) ?? defaults.taxPct;
  const example =
    f.deal === 'COMMISSION'
      ? t('sp.exampleCOMMISSION', { sum: amd(ex), ours: amd(Math.floor((ex * num(f.commissionPct)) / 100)), partner: amd(ex - Math.floor((ex * num(f.commissionPct)) / 100)) })
      : (() => {
          const fotS = Math.floor((ex * fot) / 100);
          const taxS = Math.floor((ex * tax) / 100);
          const profit = Math.max(0, ex - fotS - taxS);
          const p = Math.floor((profit * num(f.profitSharePct)) / 100);
          return t('sp.examplePROFIT_SHARE', { sum: amd(ex), fot: amd(fotS), tax: amd(taxS), profit: amd(profit), partner: amd(p), ours: amd(profit - p) });
        })();
  const save = useMutation(
    trpc.bo.partners.saveServicePartner.mutationOptions({
      onSuccess: (r) => {
        void qc.invalidateQueries({ queryKey: trpc.bo.partners.servicePartners.queryKey() });
        onDone?.();
        onClose();
        if (!partner) navigate(`/service-partners/${r.id}`);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={partner ? t('sp.editTitle') : t('sp.add')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f.name.trim() || save.isPending}
            onClick={() =>
              save.mutate({
                id: partner?.id,
                name: f.name,
                contactPhone: f.contactPhone || null,
                note: f.note || null,
                active: f.active,
                deal: f.deal,
                commissionPct: num(f.commissionPct),
                profitSharePct: num(f.profitSharePct),
                fotPct: opt(f.fotPct),
                taxPct: opt(f.taxPct),
              })
            }
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('crm.name')}>
          <input className="bo-input" value={f.name} maxLength={100} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('sp.phone')}>
          <input className="bo-input" value={f.contactPhone} maxLength={30} onChange={(e) => setF({ ...f, contactPhone: e.target.value })} />
        </Field>
      </div>
      <Field label={t('sp.service')}>
        <input className="bo-input" value={t('sp.cleaningOnly')} disabled />
      </Field>
      <Field label={t('sp.deal')}>
        <div className="bo-chtabs">
          {(['COMMISSION', 'PROFIT_SHARE'] as const).map((d) => (
            <button key={d} type="button" aria-pressed={f.deal === d} onClick={() => setF({ ...f, deal: d })}>
              {t(`sp.deal.${d}`)}
            </button>
          ))}
        </div>
      </Field>
      <p className="bo-hint" style={{ marginTop: -4 }}>
        {t(`sp.dealHint.${f.deal}`)}
      </p>
      {f.deal === 'COMMISSION' ? (
        <Field label={t('sp.commissionPct')}>
          <input className="bo-input" inputMode="numeric" value={f.commissionPct} onChange={(e) => setF({ ...f, commissionPct: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
        </Field>
      ) : (
        <>
          <Field label={t('sp.profitSharePct')}>
            <input className="bo-input" inputMode="numeric" value={f.profitSharePct} onChange={(e) => setF({ ...f, profitSharePct: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
          </Field>
          <div className="bo-row2">
            <Field label={t('sp.fotPct')} hint={t('sp.defaultHint', { n: defaults.fotPct })}>
              <input className="bo-input" inputMode="numeric" placeholder={String(defaults.fotPct)} value={f.fotPct} onChange={(e) => setF({ ...f, fotPct: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
            </Field>
            <Field label={t('sp.taxPct')} hint={t('sp.defaultHint', { n: defaults.taxPct })}>
              <input className="bo-input" inputMode="numeric" placeholder={String(defaults.taxPct)} value={f.taxPct} onChange={(e) => setF({ ...f, taxPct: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
            </Field>
          </div>
        </>
      )}
      <p className="bo-side-panel__note" style={{ margin: '0 0 12px' }}>
        {example}
      </p>
      <Field label={t('crm.note')}>
        <textarea className="bo-textarea" style={{ minHeight: 56 }} value={f.note} maxLength={2000} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('sp.activeLabel')}
      </label>
    </Dialog>
  );
}

function PartnerPayout({ id, balance, onClose, onDone }: { id: string; balance: number; onClose: () => void; onDone: () => void }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [amount, setAmount] = useState(String(balance));
  const [note, setNote] = useState('');
  const save = useMutation(
    trpc.bo.partners.servicePartnerPayout.mutationOptions({
      onSuccess: () => {
        onDone();
        toast(t('prv.payoutDone'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('sp.payoutTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!Number(amount) || save.isPending} onClick={() => save.mutate({ id, amount: Number(amount), note: note || undefined })}>
            {t('prv.payoutConfirm')}
          </button>
        </>
      }
    >
      <Field label={t('prv.amount')} hint={t('ag.balanceHint', { sum: amd(balance) })}>
        <input className="bo-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label={t('prv.payoutNote')}>
        <input className="bo-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={t('prv.payoutNotePh')} />
      </Field>
    </Dialog>
  );
}
